import React, { useEffect } from 'react';
import { Phone, CheckCircle, XCircle, Clock, Target, Award } from 'lucide-react';

const BookConsultation = () => {
  // Load Calendly widget script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const duringCall = [
    "Review how calls, inquiries, and follow-ups are handled today",
    "Identify gaps that may be costing you leads, time, or revenue",
    "Determine whether an AI & Customer Experience System Audit is the right next step"
  ];

  const whoFor = [
    "Growing service-based businesses",
    "Nonprofits with increasing inquiry volume",
    "Leaders looking to modernize customer experience systems",
    "Organizations ready to take action in the next 30–60 days"
  ];

  const whoNotFor = [
    "Looking for DIY tools or quick fixes",
    "Price shopping without readiness to invest",
    "Not responsible for decision-making",
    "Just \"curious\" about AI without a real use case"
  ];

  const walkAwayWith = [
    "A clear understanding of where your system is breaking",
    "Insight into what should be automated first",
    "A recommendation on whether the System Audit or implementation makes sense"
  ];

  const howToPrepare = [
    "How your calls and inquiries are currently handled",
    "Where you're experiencing delays, drop-offs, or inefficiencies",
    "What prompted you to book this call now"
  ];

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#227AFF]/10 mb-6">
                <Target className="h-8 w-8 text-[#227AFF]" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4">
                Identify Where Your Customer Experience Is <span className="text-gradient">Leaking Revenue</span> — and What to Fix First
              </h1>
              <div className="accent-line"></div>
            </div>

            {/* Introduction */}
            <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
              <p className="text-lg text-gray-700 mb-4">
                This strategy call is designed to help you understand where your current customer experience systems are breaking — and how automation can improve response time, professionalism, and follow-up.
              </p>
              <div className="border-t border-gray-200 pt-6 mt-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  During this call, we will:
                </p>
                <ul className="space-y-3">
                  {duringCall.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-[#227AFF] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-r from-[#227AFF]/5 to-[#007BFF]/5 rounded-xl p-6 mt-6">
                <p className="text-lg font-semibold text-gray-800 mb-2">
                  This call is not a sales pitch.
                </p>
                <p className="text-gray-600">
                  If there's a strong fit, we'll outline clear next steps. If not, you'll still leave with clarity.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Who This Call Is For */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center space-x-2 mb-4">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                  <h3 className="text-xl font-semibold">Who This Call Is For</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">This call is ideal for:</p>
                <ul className="space-y-3">
                  {whoFor.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Who This Call Is NOT For */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center space-x-2 mb-4">
                  <XCircle className="h-6 w-6 text-red-500" />
                  <h3 className="text-xl font-semibold">Who This Call Is Not For</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">This call is not a fit if you are:</p>
                <ul className="space-y-3">
                  {whoNotFor.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <XCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* What You'll Walk Away With */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 mb-8">
              <div className="flex items-center space-x-2 mb-4">
                <Award className="h-6 w-6 text-[#227AFF]" />
                <h3 className="text-xl font-semibold">What You'll Walk Away With</h3>
              </div>
              <p className="text-gray-600 mb-4">By the end of the call, you'll have:</p>
              <ul className="space-y-3">
                {walkAwayWith.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#227AFF] mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* How to Prepare */}
            <div className="bg-white border-l-4 border-[#227AFF] rounded-lg p-6 mb-8 shadow-sm">
              <div className="flex items-center space-x-2 mb-4">
                <Clock className="h-6 w-6 text-[#227AFF]" />
                <h3 className="text-xl font-semibold">How to Prepare</h3>
              </div>
              <p className="text-gray-600 mb-4">
                To make the most of our time together, please come prepared to discuss:
              </p>
              <ul className="space-y-3">
                {howToPrepare.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-[#227AFF] mr-3 font-bold">•</span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Authority Cue */}
            <div className="text-center mb-8">
              <p className="text-gray-500 text-sm italic">
                Built and led by a senior project and systems strategist with 15+ years of implementation experience across complex environments.
              </p>
            </div>

            {/* Calendly Embed */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-[#0A1538] text-white text-center py-6">
                <h2 className="text-2xl font-bold mb-2">Schedule Your Strategy Call</h2>
                <p className="text-gray-300">Select a time that works best for you</p>
              </div>
              
              {/* Calendly Inline Widget */}
              <div 
                className="calendly-inline-widget" 
                data-url="https://calendly.com/pecaroconsulting/30min?hide_gdpr_banner=1&primary_color=227AFF"
                style={{ minWidth: '320px', height: '700px' }}
              ></div>
            </div>

            {/* Contact Alternative */}
            <div className="text-center mt-8 p-6 bg-gray-100 rounded-lg">
              <p className="text-gray-700 mb-2">
                Prefer to reach out directly?
              </p>
              <p className="text-lg">
                <a href="tel:1-877-331-8445" className="text-[#227AFF] hover:underline font-semibold">
                  Call 1-877-331-8445
                </a>
                {' or '}
                <a href="mailto:info@pecaroconsulting.com" className="text-[#227AFF] hover:underline font-semibold">
                  Email us
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookConsultation;
